import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { onAuthStateChanged } from "firebase/auth";

import { auth, ensureAuthPersistence } from "../Firebase/Firebase";
import LegacyUser from "../../../shared/data/user/User";
import { EventRegister } from "../../../shared/utils/EventListeners";
import { EventUserAuthDB } from "../../../shared/utils/Constants";

interface AuthProviderProps {
  children: React.ReactNode;
}

type SessionUser = {
  uid: string;
  email: string | null;
};

export type AppUserSnapshot = {
  uid: string;
  email: string;
  profile: string | undefined;
  subscriptionStatus: "free" | "premium";
  subscriptionExpiresAt: number | null;
};

export type AuthState =
  | { status: "initializing"; user: null; profileStatus: "idle"; appUser: null }
  | { status: "authenticated"; user: SessionUser; profileStatus: "loading"; appUser: null }
  | { status: "authenticated"; user: SessionUser; profileStatus: "ready"; appUser: AppUserSnapshot }
  | { status: "anonymous"; user: null; profileStatus: "idle"; appUser: null }
  | { status: "error"; user: null; profileStatus: "idle"; appUser: null; error: Error };

const AuthContext = createContext<AuthState | undefined>(undefined);

const STARTUP_TIMEOUT_MS = 15000;
const MAX_TIMEOUT_MS = 2147000000;

function readAppUserSnapshot(uid: string): AppUserSnapshot | null {
  const legacyUser = LegacyUser.getInstance();
  if (!legacyUser.uid || legacyUser.uid !== uid || legacyUser.profile === undefined) return null;

  const expiresAt = Number(legacyUser.sub?.expires_date);
  return {
    uid: legacyUser.uid,
    email: legacyUser.email,
    profile: legacyUser.profile,
    subscriptionStatus: legacyUser.isPremium() ? "premium" : "free",
    subscriptionExpiresAt: Number.isFinite(expiresAt) ? expiresAt : null,
  };
}

export const AuthProvider = (props: AuthProviderProps) => {

  const { children } = props;

  const [state, setState] = useState<AuthState>({
    status: "initializing",
    user: null,
    profileStatus: "idle",
    appUser: null
  });

  const premiumExpiresAt = ((state.status === "authenticated") && (state.profileStatus === "ready")) ? state.appUser.subscriptionExpiresAt : null;

  useEffect(() => {
    if (premiumExpiresAt === null) return;

    let timeoutId: number | undefined;
    const refreshAtExpiration = () => {
      const remaining = premiumExpiresAt - Date.now();
      if (remaining > 0) {
        timeoutId = window.setTimeout(refreshAtExpiration, Math.min(remaining + 50, MAX_TIMEOUT_MS));
        return;
      }
      setState(previous => previous.status === "authenticated" && previous.profileStatus === "ready"
        ? {
            ...previous,
            appUser: {
              ...previous.appUser,
              subscriptionStatus: "free",
            },
          }
        : previous);
    };

    refreshAtExpiration();
    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [premiumExpiresAt]);

  useEffect(() => {
    let active = true;

    // Un démarrage trop long devient une erreur d'attente,
    // jamais une preuve d'absence de session.
    const timeoutId = window.setTimeout(() => {
      if (!active) return;

      setState((previous) =>
        previous.status === "initializing"
          ? {
              status: "error",
              user: null,
              profileStatus: "idle",
              appUser: null,
              error: new Error(
                "La vérification de la session prend trop de temps."
              ),
            }
          : previous
      );
    }, STARTUP_TIMEOUT_MS);

    let unsubscribe = () => {};

    const publishAppUser = () => {
      setState(previous => {
        if (previous.status !== "authenticated") return previous;
        const appUser = readAppUserSnapshot(previous.user.uid);
        if (appUser === null) return previous;
        return {
          ...previous,
          profileStatus: "ready",
          appUser,
        };
      });
    };
    const profileListener = EventRegister.addEventListener(EventUserAuthDB, publishAppUser);
    const subscriptionListener = EventRegister.addEventListener("userSubUpdated", publishAppUser);

    ensureAuthPersistence().then(() => {
      if (!active) return;

      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (!active) return;

        window.clearTimeout(timeoutId);

        // Pont temporaire pour les écrans existants.
        // Le contexte devient la référence pour le nouveau code.
        LegacyUser.getInstance().currentUser = firebaseUser;

        if (firebaseUser === null) {
          setState({
            status: "anonymous",
            user: null,
            profileStatus: "idle",
            appUser: null,
          });
        } else {
          const user = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
          };
          setState(previous => previous.status === "authenticated"
            && previous.user.uid === firebaseUser.uid
            && previous.profileStatus === "ready"
            ? { ...previous, user }
            : {
                status: "authenticated",
                user,
                profileStatus: "loading",
                appUser: null,
              });
        }
      });
    });

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
      unsubscribe();
      if (profileListener) EventRegister.removeEventListener(profileListener);
      if (subscriptionListener) EventRegister.removeEventListener(subscriptionListener);
    };
  }, []);

  return (
    <AuthContext.Provider value={state}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthSession(): AuthState {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error(
      "useAuthSession doit être utilisé dans un AuthProvider."
    );
  }

  return context;
}

export function useOptionalAuthSession(): AuthState | undefined {
  return useContext(AuthContext);
}
